package com.daytimegaming.ourdailyfinances.presentation.dashboard

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.AccountBalance
import androidx.compose.material.icons.filled.Bolt
import androidx.compose.material.icons.filled.Group
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.domain.model.DashboardAccount
import com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.presentation.components.BalanceTimeframe
import com.daytimegaming.ourdailyfinances.presentation.components.DashboardBalanceOverTimeCard
import com.daytimegaming.ourdailyfinances.presentation.home.getRecentSpendingCategoryBreakdownKey
import com.daytimegaming.ourdailyfinances.presentation.preview.DashboardDetailPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import com.daytimegaming.ourdailyfinances.presentation.util.toCurrencySymbol
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun DashboardDetailScreen(
    dashboardId: String,
    onNavigateBack: () -> Unit,
    onConfigureDashboard: (String) -> Unit,
    onViewAllTransactions: (String) -> Unit,
    onAccountClick: (String) -> Unit,
    onRealEstateHoldingClick: (String) -> Unit,
    onTransactionClick: (String) -> Unit,
    onInvalidAccess: (() -> Unit)? = null
) {
    val viewModel = koinViewModel<DashboardDetailViewModel>(key = dashboardId) { parametersOf(dashboardId) }
    val state by viewModel.state.collectAsStateWithLifecycle()
    val selectedTimeframe by viewModel.selectedTimeframe.collectAsStateWithLifecycle()

    LaunchedEffect(state) {
        if (state is DashboardDetailScreenState.Error && onInvalidAccess != null) {
            onInvalidAccess()
        }
    }

    DashboardDetailScreenContent(
        state = state,
        dashboardId = dashboardId,
        onNavigateBack = onNavigateBack,
        onConfigureDashboard = onConfigureDashboard,
        onViewAllTransactions = onViewAllTransactions,
        onAccountClick = onAccountClick,
        onRealEstateHoldingClick = onRealEstateHoldingClick,
        onTransactionClick = onTransactionClick,
        selectedTimeframe = selectedTimeframe,
        onTimeframeSelected = viewModel::setSelectedTimeframe
    )
}

@Composable
fun DashboardDetailScreenContent(
    state: DashboardDetailScreenState,
    dashboardId: String,
    onNavigateBack: () -> Unit,
    onConfigureDashboard: (String) -> Unit,
    onViewAllTransactions: (String) -> Unit,
    onAccountClick: (String) -> Unit,
    onRealEstateHoldingClick: (String) -> Unit,
    onTransactionClick: (String) -> Unit,
    selectedTimeframe: BalanceTimeframe = BalanceTimeframe.SixMonths,
    onTimeframeSelected: (BalanceTimeframe) -> Unit = {}
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    val title = (state as? DashboardDetailScreenState.Loaded)?.detail?.name ?: "Dashboard"
                    Text(title)
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                    }
                },
                actions = {
                    IconButton(onClick = { onConfigureDashboard(dashboardId) }) {
                        Icon(Icons.Default.Settings, contentDescription = "Configure Dashboard")
                    }
                }
            )
        },
    ) { innerPadding ->
        when (val s = state) {
            is DashboardDetailScreenState.Loading -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                }
            }
            is DashboardDetailScreenState.Error -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = s.message,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodyMedium,
                    )
                }
            }
            is DashboardDetailScreenState.Loaded -> {
                val detail = s.detail
                val accounts = detail.accounts
                val realEstateHoldings = detail.realEstateHoldings
                val totalBalance = accounts.sumOf {
                    val rawBal = it.currentBalance ?: 0.0
                    val isInvestment = it.type.trim().lowercase() == "investment"
                    val bal = if (isInvestment && rawBal <= 0.0) {
                        val acctHoldings = s.holdings.filter { h -> h.accountId == it.accountId }
                        val holdingsVal = acctHoldings.sumOf { h -> h.institutionValue ?: (h.quantity * h.institutionPrice) }
                        if (holdingsVal > 0.0) holdingsVal else rawBal
                    } else {
                        rawBal
                    }
                    if (it.type.trim().lowercase() == "credit" || it.type.trim().lowercase() == "loan") -bal else bal
                } + realEstateHoldings.sumOf { holding ->
                    holding.netValue
                }
                val isNegative = totalBalance < 0
                val absBalance = kotlin.math.abs(totalBalance)
                val formattedBalance = absBalance.formatAmount()
                val parts = formattedBalance.split('.')
                val intPart = parts.getOrElse(0) { "0" }
                val decPart = parts.getOrNull(1) ?: "00"

                LazyColumn(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    // Combined Balance Hero Section
                    item {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 12.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(
                                text = "Total Combined Balance",
                                style = MaterialTheme.typography.labelSmall.copy(letterSpacing = 2.sp),
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                fontWeight = FontWeight.Bold
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            Row(
                                verticalAlignment = Alignment.Bottom
                            ) {
                                if (isNegative) {
                                    Text(
                                        text = "-",
                                        style = MaterialTheme.typography.titleLarge,
                                        color = MaterialTheme.colorScheme.error,
                                        modifier = Modifier.padding(bottom = 12.dp)
                                    )
                                }
                                Text(
                                    text = "$",
                                    style = MaterialTheme.typography.titleLarge,
                                    color = MaterialTheme.colorScheme.primary,
                                    modifier = Modifier.padding(bottom = 12.dp)
                                )
                                Text(
                                    text = intPart,
                                    style = MaterialTheme.typography.displayLarge.copy(fontSize = 48.sp),
                                    color = MaterialTheme.colorScheme.onSurface,
                                    fontWeight = FontWeight.Bold
                                )
                                Text(
                                    text = ".$decPart",
                                    style = MaterialTheme.typography.titleLarge,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    modifier = Modifier.padding(bottom = 8.dp)
                                )
                            }
                            if (detail.members.isNotEmpty()) {
                                Spacer(modifier = Modifier.height(8.dp))

                                Row(
                                    horizontalArrangement = Arrangement.spacedBy(2.dp),
                                ) {
                                    detail.members.forEachIndexed { index, member ->
                                        val initials = if (!member.firstName.isNullOrBlank() && !member.lastName.isNullOrBlank()) {
                                            "${member.firstName.take(1)}${member.lastName.take(1)}".uppercase()
                                        } else if (!member.firstName.isNullOrBlank()) {
                                            member.firstName.take(2).uppercase()
                                        } else if (!member.email.isNullOrBlank()) {
                                            member.email.take(2).uppercase()
                                        } else {
                                            "M${index + 1}"
                                        }

                                        MemberRowItem(initials = initials)
                                    }
                                }
                            }
                        }
                    }

                    // Balance over time graph
                    item {
                        DashboardBalanceOverTimeCard(
                            accounts = accounts,
                            transactions = s.detail.transactions,
                            holdings = s.holdings,
                            securityPerformance = s.securityPerformance,
                            realEstateHoldings = realEstateHoldings,
                            selectedTimeframe = selectedTimeframe,
                            onTimeframeSelected = onTimeframeSelected,
                            isSecurityPerformanceLoading = s.isSecurityPerformanceLoading,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }

                    // Accounts list header
                    item {
                        Text(
                            text = "Linked Accounts",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface,
                            modifier = Modifier.padding(top = 8.dp)
                        )
                    }

                    // Accounts items
                    if (accounts.isEmpty() && realEstateHoldings.isEmpty()) {
                        item {
                            GlassCard(modifier = Modifier.fillMaxWidth()) {
                                Text(
                                    text = "No accounts or real estate holdings linked to this dashboard.",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }
                    } else {
                        items(accounts, key = { it.accountId }) { account ->
                            LinkedAccountRow(
                                account = account,
                                onClick = { onAccountClick(account.accountId) }
                            )
                        }

                        items(realEstateHoldings, key = { it.realEstateHoldingId }) { holding ->
                            LinkedRealEstateRow(
                                holding = holding,
                                onClick = { onRealEstateHoldingClick(holding.realEstateHoldingId) }
                            )
                        }
                    }

                    // Transactions Section (Recent Activity)
                    item {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = 8.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = "Recent Activity",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                            Text(
                                text = "View All",
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.clickable { onViewAllTransactions(dashboardId) }
                            )
                        }
                    }

                    if (detail.transactions.isEmpty()) {
                        item {
                            GlassCard(modifier = Modifier.fillMaxWidth()) {
                                Text(
                                    text = "No recent transactions.",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }
                    } else {
                        items(detail.transactions.take(10), key = { it.transactionId }) { txn ->
                            val account = detail.accounts.find { it.accountId == txn.accountId }
                            TransactionRowItem(
                                transaction = txn,
                                accountName = account?.displayName,
                                institutionName = account?.institutionName,
                                onClick = { onTransactionClick(txn.transactionId) }
                            )
                        }
                    }
                }
            }
        }
    }
}

@PreviewLightDark
@Composable
fun DashboardDetailScreenPreview(
    @PreviewParameter(DashboardDetailPreviewParameterProvider::class) state: DashboardDetailScreenState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
            DashboardDetailScreenContent(
                state = state,
                dashboardId = "db-1",
                onNavigateBack = {},
                onConfigureDashboard = {},
                onViewAllTransactions = {},
                onAccountClick = {},
                onRealEstateHoldingClick = {},
                onTransactionClick = {}
            )
        }
    }
}

@Composable
private fun BreakdownItem(
    title: String,
    amount: Double,
    isNegative: Boolean = false,
    modifier: Modifier = Modifier
) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = Color(0x11FFFFFF)
        ),
        shape = RoundedCornerShape(8.dp),
        modifier = modifier.border(1.dp, Color(0x1AFFFFFF), RoundedCornerShape(8.dp))
    ) {
        Column(
            modifier = Modifier.padding(12.dp)
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = "${if (isNegative) "-" else ""}$${amount.formatAmount()}",
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = if (isNegative) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurface
            )
        }
    }
}

@Composable
private fun MemberRowItem(
    initials: String,
) {
    Box(
        modifier = Modifier
            .size(25.dp)
            .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.2f), CircleShape),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = initials,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.primary,
        )
    }
}

@Composable
private fun LinkedAccountRow(
    account: DashboardAccount,
    onClick: () -> Unit
) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = Color(0x11FFFFFF)
        ),
        shape = RoundedCornerShape(8.dp),
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() }
            .border(1.dp, Color(0x1AFFFFFF), RoundedCornerShape(8.dp))
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = account.displayName,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                account.institutionName?.let { institution ->
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        text = institution,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                account.subtype?.let {
                    Spacer(modifier = Modifier.height(4.dp))
                    Box(
                        modifier = Modifier
                            .background(Color(0x1FBEC6E0), shape = RoundedCornerShape(999.dp))
                            .padding(horizontal = 8.dp, vertical = 2.dp)
                    ) {
                        Text(
                            text = it.uppercase(),
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.secondary
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.width(8.dp))
            account.currentBalance?.let { balance ->
                Text(
                    text = "${account.isoCurrencyCode.toCurrencySymbol()}${balance.formatAmount()}",
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
            }
        }
    }
}

@Composable
private fun LinkedRealEstateRow(
    holding: RealEstateHolding,
    onClick: () -> Unit
) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = Color(0x11FFFFFF)
        ),
        shape = RoundedCornerShape(8.dp),
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, Color(0x1AFFFFFF), RoundedCornerShape(8.dp))
            .clickable { onClick() }
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = holding.nickname,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Spacer(modifier = Modifier.height(2.dp))
                val addressText = buildString {
                    append(holding.addressLine1)
                    holding.addressLine2?.let {
                        if (it.isNotEmpty()) append(", ").append(it)
                    }
                    append(", ").append(holding.city).append(", ").append(holding.state).append(" ").append(holding.zipCode)
                }
                Text(
                    text = addressText,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Spacer(modifier = Modifier.height(4.dp))
                Box(
                    modifier = Modifier
                        .background(Color(0x1FBEC6E0), shape = RoundedCornerShape(999.dp))
                        .padding(horizontal = 8.dp, vertical = 2.dp)
                ) {
                    Text(
                        text = "REAL ESTATE",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.secondary
                    )
                }
            }
            Spacer(modifier = Modifier.width(8.dp))
            val netValue = holding.netValue
            val netValueText = buildString {
                if (netValue < 0) {
                    append("-")
                }
                append("$")
                append(kotlin.math.abs(netValue).formatAmount())
            }
            Text(
                text = netValueText,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
        }
    }
}

@Composable
private fun TransactionRowItem(
    transaction: Transaction,
    accountName: String?,
    institutionName: String?,
    onClick: () -> Unit
) {
    val category = transaction.getRecentSpendingCategoryBreakdownKey()
    val icon = when {
        category.contains("Groceries", ignoreCase = true) || category.contains("Food", ignoreCase = true) -> Icons.Default.Group // mapped representation
        category.contains("Utilities", ignoreCase = true) || category.contains("Electric", ignoreCase = true) -> Icons.Default.Bolt
        category.contains("Income", ignoreCase = true) || category.contains("Transfer", ignoreCase = true) -> Icons.Default.AccountBalance
        else -> Icons.Default.Bolt
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(Color(0x06FFFFFF))
            .border(1.dp, Color(0x0AFFFFFF), RoundedCornerShape(8.dp))
            .clickable { onClick() }
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(40.dp)
                .background(MaterialTheme.colorScheme.surfaceContainerHighest, CircleShape),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(20.dp)
            )
        }
        Spacer(modifier = Modifier.width(12.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = transaction.name,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            val details = listOfNotNull(accountName, institutionName).joinToString(" • ")
            if (details.isNotEmpty()) {
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = details,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = transaction.date,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f)
            )
        }
        Spacer(modifier = Modifier.width(8.dp))
        Column(horizontalAlignment = Alignment.End) {
            val isExpense = transaction.amount > 0
            Text(
                text = "${if (isExpense) "-" else "+"}$${kotlin.math.abs(transaction.amount).formatAmount()}",
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = if (isExpense) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.primary
            )
            Text(
                text = category,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

